import { useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import FullCalendar from '@fullcalendar/react'; // must go before plugins
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMoneyBillAlt as iconMoney,
  faUserFriends as iconMultiUsers
} from '@fortawesome/free-solid-svg-icons';
import AdminSchedule from '../Admin/AdminSchedule';
import Loading from '../../components/Loading';
import AdminModal, { ModalRefObject } from '../../components/Modal';
import { useUserContext } from '../../hooks/userHook';
import { getCurrentWindow } from '../../hooks/windowSizeHook';
import { FullCalendarEventContent, FullCalendarEventInfo } from '../../models';
import { getEventDisplay, getEventsFiltered } from './schedule';
import { privilegedUser } from '../../utils/user';
import './Book.scss';

export default function Book() {
  const { bookDate = null } = useParams<{ bookDate: string }>();
  const { user } = useUserContext();
  const privileged = privilegedUser(user);
  const navigate = useNavigate();
  const calendarRef = useRef<any>(null);
  const loadingRef = useRef<any>(null);
  const modalRef = useRef<ModalRefObject>(null);

  const toggleSpinner = useCallback((loading: boolean) => loadingRef?.current?.show(loading), [loadingRef]);

  const initialDate = moment(bookDate, 'YYYYMMDD').isValid() ? moment(bookDate, 'YYYYMMDD').format('YYYY-MM-DD').toString() : undefined;

  /**
   * =================================================
   * FUNCTION TO DISPLAY EVENT CONTENT
   * =================================================
   * */
  function renderEventContent(eventInfo: FullCalendarEventContent) {
    // Let's simplify the evenInfo object
    const event = {
      ...eventInfo.event,
      ...eventInfo.event.extendedProps,
      id: eventInfo.event.id,
    };

    // Getting key display details in one shot
    const displayDetails = getEventDisplay({
      booked: event.booked,
      locked: event.locked,
      published: event.published
    });

    // Event in the past check
    const eventInThePast = moment().isAfter(eventInfo.event.start);

    return (
      <div className={`schedule-event-container px-2 ${(!event.booked
        && !event.locked
        && !eventInThePast
      ) || user ? 'cursor-pointer' : 'cursor-auto'}
          ${eventInThePast ? ' expired-slot' : ''}
      `}
      >
        <div className="d-flex flex-row justify-content-between align-items-center container">
          <span className="me-2">{eventInfo.timeText}</span>
          <span className="text-ellipsis">
            {
              event.title
                ? event.title
                : ''
            }
          </span>
          <div className="d-flex flex-row justify-content-end align-items-center flex-wrap">
            {user && <FontAwesomeIcon icon={displayDetails.adminIcon} className="ms-2" />}
            <FontAwesomeIcon className="ms-2" icon={displayDetails.statusIcon} color={displayDetails.borderColor || ''} />
          </div>
        </div>

        {event.bookingInfo && (
          <div className="d-flex flex-row justify-content-between align-items-center container">
            <span className="text-ellipsis">
              {`
              ${event.bookingInfo.participant?.[0]?.participantFirstName || ''}
              ${event.bookingInfo.participant?.[0]?.participantLastName || ''}
              `}
              {
                event.bookingInfo.participant && event.bookingInfo.participant.length > 1
                  ? <FontAwesomeIcon icon={iconMultiUsers} className="ms-2" size="sm" />
                  : ''
              }
            </span>
            {user
              && (
                <span>
                  <FontAwesomeIcon
                    icon={iconMoney}
                    color={`${event?.bookingInfo?.paid
                      ? '#28ad00'
                      : 'red'}`} />
                </span>
              )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div id="schedule-app-booking-container">
      <h3 className="mb-4">Available Sessions</h3>

      <Loading ref={loadingRef} />

      <FullCalendar // https://fullcalendar.io/docs#toc
        allDaySlot={false}
        buttonIcons={{
          today: ' fas fa-calendar-day',
          prev: ' fas fa-angle-left',
          next: ' fas fa-angle-right',
          timeGridWeek: ' fas fa-calendar-week',
          timeGridDay: ' fas fa-calendar-day',
          refreshButton: ' fas fa-redo-alt',
        }}
        customButtons={{
          refreshButton: {
            text: 'refresh',
            click: () => calendarRef?.current?.getApi().refetchEvents()
          }
        }}
        eventClick={(e) => {
          const iAmClickable = e.event.id
            && moment().isBefore(e.event.start)
            && !e.event.extendedProps.booked
            && !e.event.extendedProps.locked;

          if (privileged) {
            modalRef?.current?.updateBody(<AdminSchedule id={e.event.id || ''} />);
            return modalRef?.current?.show();
          }

          if (iAmClickable) {
            return navigate(`/claim/${e.event.id}`);
          }
        }}
        eventContent={renderEventContent}
        events={async (info: any) => {
          const events = await getEventsFiltered(info.start, info.end, user) as any;
          console.log('EVENTS: ', events, info);

          // Default value with our default time set (remember to add 1 hour, since we are subtracting it below)
          const initialValue = events.length > 0 ? events[0] : {
            id: undefined,
            start: '2022-01-21T23:00:00Z', // 5:00 PM (default start time will be 4:00 PM)
            end: null,
            title: null,
            allDay: false,
            editable: false,
            duration: 30,
            backgroundColor: null,
            borderColor: null,
            extendedProps: {
              booked: false,
              locked: false,
              published: false,
              recurrenceId: null,
            }
          };

          // calculate the minTime based on the array of events returned (set based )
          const minTime = events.length > 0
            ? moment(
              events.reduce(
                (previousValue: FullCalendarEventInfo, currentValue: FullCalendarEventInfo) => moment(moment(previousValue.start).format('HH:mm:ss'), 'HH:mm:ss').isSameOrBefore(moment(moment(currentValue.start).format('HH:mm:ss'), 'HH:mm:ss')) ? previousValue : currentValue,
                initialValue
              ).start).subtract(30, 'minutes').startOf('hour').format('HH:mm')
            : '12:00';

          // calculate the maxTime based on the array of events returned (set based )
          const maxTime = events.length > 0
            ? moment(
              events.reduce(
                (previousValue: FullCalendarEventInfo, currentValue: FullCalendarEventInfo) => moment(moment(previousValue.start).format('HH:mm:ss'), 'HH:mm:ss').isSameOrAfter(moment(moment(currentValue.start).format('HH:mm:ss'), 'HH:mm:ss')) ? previousValue : currentValue,
                initialValue
              ).start).add(30, 'minutes').endOf('hour').format('HH:mm')
            : '20:00';

          // update the calendar display with the above calculated minTime & maxTime
          calendarRef?.current?.getApi().setOption('slotMinTime', minTime);
          calendarRef?.current?.getApi().setOption('slotMaxTime', maxTime); // causes display to expand, disable for now

          // return the filtered events
          return events;
        }} // https://fullcalendar.io/docs/events-function
        eventTextColor="#303030"
        eventTimeFormat={{
          hour: 'numeric',
          minute: '2-digit',
          meridiem: 'narrow'
        }}
        expandRows={true}
        firstDay={1} // integer of the start of the week, default: 0 (Sunday=0, Monday=1, Tuesday=2, etc.)
        initialDate={initialDate}
        headerToolbar={{
          left: 'prev,next,today',
          center: 'title',
          right: 'refreshButton,timeGridWeek,timeGridDay',
        }}
        height="95%"
        // hiddenDays={[0]} // array of days of the week to hide (i.e. 0 = sunday)
        initialView={getCurrentWindow().mobileView ? 'timeGridDay' : 'timeGridWeek'}
        loading={(loading) => toggleSpinner(loading)}
        nowIndicator={true}
        plugins={[
          dayGridPlugin,
          timeGridPlugin,
          listPlugin // let's figure out how to implement this for admin/instructor view -- https://fullcalendar.io/docs/list-view
        ]}
        ref={calendarRef}
        scrollTime={moment().format('HH:mm')}
        scrollTimeReset={false}
        slotMinWidth={200}
        // slotMinTime={'08:00'} // what time to start the time view, hides the rest
        // slotMaxTime={'22:00'} // what time to end the time view, hides the rest
        views={{
          timeGridWeek: {
            titleFormat: {
              month: 'long',
              day: 'numeric',
              year: 'numeric'
            }
          },
          timeGridDay: {
            titleFormat: {
              month: 'numeric',
              day: 'numeric',
              year: 'numeric'
            }
          }
        }}
        windowResize={({ view }) => {
          const currentView = view?.type;
          const suggestedView = getCurrentWindow().mobileView ? 'timeGridDay' : 'timeGridWeek';

          if (suggestedView !== currentView) {
            calendarRef?.current?.getApi().changeView(suggestedView);
          }
        }}
      />
      <AdminModal
        ref={modalRef}
        id="schedule-admin-modal"
        header="Admin"
        closeCallback={() => calendarRef?.current?.getApi().refetchEvents()}
        centered
      />
    </div>
  );
}
